import { apiService } from './api'
import type { User } from '@/types/user'
import type { PdqDesignItemSummary, PdqDesignJobSpec } from '@/types/pdqDesignJob'
import type { PrintBridgeAttachment } from '@/types/printBridge'

export type DesignJobGroupingMode = 'product' | 'submission'

export type ApprovalWorkflow = 'legacy_stages' | 'pdq_queue'

export interface DesignJobApprovalQueueStep {
  uuid?: string | null
  queue_approver_uuid: string
  order_index: number
  email: string
  group: 'internal' | 'external'
  assigned_user_uuid?: string | null
  assigned_user_name?: string | null
  status: 'waiting' | 'active' | 'approved' | 'rejected'
  approved_at?: string | null
  rejected_at?: string | null
  comment?: string | null
  rejection_reason?: string | null
  approved_by_email?: string | null
  has_approval_code?: boolean
}

export interface DesignJobApprovalQueue {
  workflow: ApprovalWorkflow
  current_cycle_number: number | null
  active_step_index: number | null
  active_step_uuid: string | null
  total_approvers: number
  approved_count: number
  progress_label: string | null
  master_approvers: Array<{
    uuid: string
    order_index: number
    email: string
    group: 'internal' | 'external'
    assigned_user_uuid?: string | null
    assigned_user_name?: string | null
  }>
  current_cycle_steps: DesignJobApprovalQueueStep[]
  prior_cycle_numbers: number[]
}

export interface DesignJobPdqDesign {
  uuid: string
  sku: string | null
  sort_order: number
  design_spec?: PdqDesignJobSpec | null
  summary?: PdqDesignItemSummary | null
  product_draft?: {
    uuid: string
    title: string
    notes: string | null
  } | null
}

export interface DesignJobPdqSubmission {
  uuid: string
  title?: string | null
  submitted_at?: string | null
  creator?: {
    uuid: string
    name: string
    email: string
  } | null
  assigned_to_team?: {
    uuid: string
    name: string
  } | null
  request_design_approvers?: import('@/types/pdq').PdqApproverInput[]
  attachments?: PrintBridgeAttachment[]
}

export interface DesignJobPdqApprover {
  name?: string | null
  email: string
  group?: string | null
  order?: number | null
}

// TypeScript types matching the backend API spec
export interface DesignJob {
  id: number
  uuid: string
  title: string
  description?: string // Legacy field, use instructions
  instructions?: string // New field replacing description
  status: 'Unassigned' | 'Assigned' | 'InProgress' | 'SupervisorApproval' | 'HeadApproval' | 'SalesApproval' | 'CustomerApproval' | 'QueueApproval' | 'PrePressRequired' | 'PrePressInProgress' | 'Completed'
  /** Queue listing label; team-routed jobs without a user show as Unassigned. */
  display_status?: string
  due_date: string
  height: number | null
  width: number | null
  depth: number | null
  allowed_file_extensions: string[] | null
  sales_rep_email_1: string
  sales_rep_email_2: string | null
  customer_email_1: string
  customer_email_2: string | null
  assigned_to_uuid: string | null
  assigned_to_team_uuid: string | null
  can_assign_to_user?: boolean
  is_approval_pending?: boolean
  can_upload_assets?: boolean
  can_reassign?: boolean
  assigned_at: string | null
  assigned_by_uuid: string | null
  created_by_uuid: string
  customer_approval_token: string | null
  customer_approval_token_expires_at: string | null
  sales_approval_code: string | null
  sales_approval_code_expires_at: string | null
  created_at: string
  updated_at: string
  deleted_at: string | null
  
  // New specification fields (using backend field names)
  quantity_each?: number | null
  quantity_total?: number | null
  location?: 'indoor' | 'outdoor' | null
  sides?: 'single' | 'double' | null
  material_type?: 'vinyl' | 'coro' | 'banner' | 'acm' | 'scm' | 'acrylic' | 'pvc' | 'poly' | 'pb' | 'whiteboard' | null
  material_subtype?: string | null
  material_thickness?: string | null
  material_print_type?: string | null
  material_whiteboard_type?: 'turn' | 'tumble' | null
  banner_stand?: string | null
  substrate_stock?: string | null
  roll_stock?: string | null
  laminate_stock?: string | null
  cut_type?: 'simple' | 'contour' | null
  kiss_cut?: boolean
  thru_cut?: boolean
  holes_size?: number | null
  holes_quantity?: number | null
  holes_note?: string | null
  weed_required?: boolean
  application_tape_required?: boolean
  color_cmyk?: boolean
  color_spot?: boolean
  color_spot_pms?: string | null
  white_ink_type?: 'flood' | 'spot' | null
  fonts_required?: boolean
  font_name?: string | null
  hardware_grommets_quantity?: number | null
  hardware_standoff_size?: number | null
  hardware_standoff_color?: string | null
  hardware_aframe?: string | null
  hardware_aframe_required?: boolean
  hardware_post?: string | null
  hardware_post_required?: boolean
  hardware_bracket?: string | null
  hardware_bracket_required?: boolean
  vhb_kinds?: string | null
  vhb_required?: boolean
  special_hardware?: string | null
  special_hardware_required?: boolean
  is_vended?: boolean
  vendor_name?: string | null
  site_survey_required?: boolean
  site_survey_uuid?: string | null
  requires_prepress?: boolean
  priority?: string
  unit_of_measure?: string
  dpi_resolution?: number | string | null
  is_design_vip?: boolean
  design_vip_approved?: boolean
  grouping_mode?: DesignJobGroupingMode
  pdq_submission_uuid?: string | null
  product_count?: number | null
  product_preview?: Array<{
    uuid: string
    title: string
    sku: string | null
  }>
  requester?: {
    uuid: string
    name: string
    email: string
  } | null
  source?: string | null
  pdq_designs?: DesignJobPdqDesign[]
  pdq_submission?: DesignJobPdqSubmission | null
  pdq_approvers?: DesignJobPdqApprover[]
  approval_workflow?: ApprovalWorkflow
  current_approval_cycle_number?: number | null
  approval_queue?: DesignJobApprovalQueue | null
  queue_progress_label?: string | null
  vip_approval_status?: {
    is_vip: boolean
    approved: boolean
    rejected?: boolean
    status_message?: string
    buttons_hidden?: boolean
    approvals_count: number
    required_approvals: number
    approvals: Array<{
      approver_uuid: string
      approver_name: string
      approver_email?: string
      approved: boolean
      approved_at?: string
      comment?: string
    }>
  }
  
  // Relationships (when loaded)
  assigned_to?: User
  assigned_to_team?: import('@/types/team').Team
  assigned_by?: User
  created_by?: User
  assets?: DesignJobAsset[]
  reference_materials_notes?: string | null
  approvals?: DesignJobApproval[]
  time_entries?: DesignJobTimeEntry[]
  events?: DesignJobEvent[]
  versions?: DesignJobVersion[]
  current_final_assets?: DesignJobAsset[]
  rejected_version_cycles?: DesignJobRejectedVersionCycle[]
}

export interface DesignJobAsset {
  id: number
  uuid: string
  asset_type: 'reference_materials' | 'final' | 'font' | 'pre_press'
  design_job_id: number
  design_job_uuid: string
  file_name: string
  file_path: string
  file_size: number
  mime_type: string
  uploaded_by_uuid: string
  created_at: string
  updated_at: string
  deleted_at: string | null
}

export interface DesignJobApproval {
  id: number
  uuid: string
  design_job_id: number
  design_job_uuid: string
  approval_stage: 'supervisor' | 'head' | 'sales' | 'customer'
  approved: boolean
  rejection_reason: string | null
  comment: string | null
  approved_by_uuid: string | null
  approved_by_email: string | null
  created_at: string
  updated_at: string
}

export interface DesignJobTimeEntry {
  id: number
  uuid: string
  design_job_id: number
  design_job_uuid: string
  user_uuid: string
  started_at: string
  ended_at: string | null
  stopped_reason: 'manual' | 'auto_submit' | null
  created_at: string
  updated_at: string
  total_seconds?: number
}

export interface DesignJobEvent {
  id: number
  uuid: string
  design_job_id: number
  design_job_uuid: string
  event_type: 'status_change' | 'assignment' | 'current_step_assignment' | 'approval' | 'rejection' | 'asset_upload' | 'asset_delete' | 'timer_start' | 'timer_stop' | 'timer_auto_stop' | 'comment' | 'field_update'
  description: string
  metadata: Record<string, any> | null
  performed_by_uuid: string | null
  performed_by_email: string | null
  created_at: string
  performed_by?: User
}

export interface DesignJobVersion {
  id: number
  uuid: string
  design_job_uuid: string
  version_number: number
  milestone: string
  snapshot_data: Record<string, any>
  created_by_uuid: string | null
  created_at: string
}

export interface DesignJobRejectedVersionCycle {
  uuid: string
  cycle_number: number
  rejection_reason: string
  rejected_at: string
  rejected_by?: User
  rejected_by_email?: string
  approval_stage: string
  assets: DesignJobAsset[]
}

export interface DesignJobsResponse {
  success: boolean
  data: DesignJob[]
  meta?: {
    queue_type: string
    total: number
    current_page?: number
    last_page?: number
    per_page?: number
    from?: number
    to?: number
  }
  message?: string
  error?: string
}

export interface DesignJobResponse {
  success: boolean
  job: DesignJob
  message?: string
  error?: string
}

export interface DesignJobFilters {
  queue?: 'unassigned' | 'my' | 'prepress' | 'completed'
  status?: string
  priority?: string
  assigned_to?: string
  search?: string
  page?: number
  per_page?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  sort_direction?: 'asc' | 'desc' // For compatibility
}

export interface TimerTotalResponse {
  success: boolean
  total_seconds: number
  total_hours: number
  current_session: DesignJobTimeEntry | null
  message?: string
  error?: string
}

export interface EventsResponse {
  success: boolean
  events: DesignJobEvent[]
  message?: string
  error?: string
}

export interface VersionsResponse {
  success: boolean
  versions: DesignJobVersion[]
  message?: string
  error?: string
}

class DesignJobService {
  private readonly endpoint = '/design-jobs'

  /**
   * Get queue counts for all queue types in a single optimized query
   */
  async getQueueCounts(): Promise<{
    success: boolean
    data?: {
      unassigned: number
      my: number
      prepress: number
    }
    message?: string
    error?: string
  }> {
    try {
      const response = await apiService.get<{
        success: boolean
        data: {
          unassigned: number
          my: number
          prepress: number
        }
      }>(`${this.endpoint}/queue-counts`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch queue counts'
      return {
        success: false,
        error: errorMessage
      }
    }
  }

  /**
   * Check for updates in queues since a given timestamp
   */
  async checkUpdates(since?: string, queues?: string[]): Promise<{
    success: boolean
    data?: {
      has_changes: boolean
      latest_updated_at: string | null
      counts: Record<string, number>
      changed_job_uuids?: string[]
    }
    message?: string
    error?: string
  }> {
    try {
      const params: Record<string, string | string[]> = {}
      if (since) {
        params.since = since
      }
      if (queues && queues.length > 0) {
        params.queues = queues
      }
      const response = await apiService.get<{
        success: boolean
        data: {
          has_changes: boolean
          latest_updated_at: string | null
          counts: Record<string, number>
          changed_job_uuids?: string[]
        }
      }>(`${this.endpoint}/updates`, { params })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to check for updates'
      return {
        success: false,
        error: errorMessage
      }
    }
  }

  /**
   * Get list of design jobs with queue filtering
   */
  async getJobs(filters?: DesignJobFilters): Promise<DesignJobsResponse> {
    const params: Record<string, string | number> = {}
    
    if (filters?.queue) {
      params.queue = filters.queue
    }
    if (filters?.status) {
      params.status = filters.status
    }
    if (filters?.priority) {
      params.priority = filters.priority
    }
    if (filters?.assigned_to) {
      params.assigned_to = filters.assigned_to
    }
    if (filters?.search) {
      params.search = filters.search
    }
    if (filters?.page) {
      params.page = filters.page
    }
    if (filters?.per_page) {
      params.per_page = filters.per_page
    }
    if (filters?.sort_by) {
      params.sort_by = filters.sort_by
    }
    // Prefer sort_order, fallback to sort_direction for compatibility
    if (filters?.sort_order) {
      params.sort_order = filters.sort_order
    } else if (filters?.sort_direction) {
      params.sort_order = filters.sort_direction
    }

    try {
      const response = await apiService.get<DesignJobsResponse>(this.endpoint, { params })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch design jobs'
      return {
        success: false,
        data: [],
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Get a single design job by UUID
   */
  async getJob(uuid: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.get<DesignJobResponse>(`${this.endpoint}/${uuid}`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Create a new design job
   */
  async createJob(data: FormData): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(this.endpoint, data)
      return response
    } catch (error: any) {
      // Extract detailed error message from backend
      const backendError = error.response?.data?.error || error.response?.data?.message
      const errorMessage = backendError || error.message || 'Failed to create design job'
      
      // Include validation errors if present
      const validationErrors = error.response?.data?.errors
      let fullErrorMessage = errorMessage
      if (validationErrors && typeof validationErrors === 'object') {
        const errorDetails = Object.entries(validationErrors)
          .map(([field, messages]) => {
            const msgArray = Array.isArray(messages) ? messages : [messages]
            return `${field}: ${msgArray.join(', ')}`
          })
          .join('; ')
        if (errorDetails) {
          fullErrorMessage = `${errorMessage} (${errorDetails})`
        }
      }
      
      return {
        success: false,
        job: {} as DesignJob,
        message: fullErrorMessage,
        error: fullErrorMessage
      }
    }
  }

  /**
   * Update a design job
   */
  async updateJob(uuid: string, data: Record<string, any>): Promise<DesignJobResponse> {
    try {
      const response = await apiService.put<DesignJobResponse>(`${this.endpoint}/${uuid}`, data)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Delete a design job (soft delete)
   */
  async deleteJob(uuid: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await apiService.delete<{ success: boolean; message?: string }>(`${this.endpoint}/${uuid}`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete design job'
      return {
        success: false,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Assign or reassign a design job (user and/or team).
   */
  async assignJob(
    uuid: string, 
    data: { assigned_to_uuid?: string; assigned_to_team_uuid?: string }
  ): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/${uuid}/assign`, data)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to assign design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  async getAssignableUsers(uuid: string): Promise<{
    success: boolean
    data?: Array<{ uuid: string; name: string; email: string }>
    message?: string
    error?: string
  }> {
    try {
      const response = await apiService.get<{
        success: boolean
        data: Array<{ uuid: string; name: string; email: string }>
      }>(`${this.endpoint}/${uuid}/assignable-users`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load assignable users'
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      }
    }
  }

  /**
   * Start work on a job (Assigned → InProgress)
   */
  async startWork(uuid: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/${uuid}/start`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to start work'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Submit job for approval (InProgress → SupervisorApproval)
   */
  async submitForApproval(uuid: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/${uuid}/submit`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit for approval'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Approve a design job (internal)
   */
  async approveJob(uuid: string, comment?: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/${uuid}/approve`, {
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to approve design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Reject a design job (internal)
   */
  async rejectJob(uuid: string, rejectionReason: string, comment?: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/${uuid}/reject`, {
        rejection_reason: rejectionReason,
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reject design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Upload an asset
   */
  async uploadAsset(uuid: string, file: File, assetType: 'inspiration' | 'reference' | 'client' | 'final'): Promise<{ success: boolean; asset?: DesignJobAsset; message?: string; error?: string }> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('asset_type', assetType)
      
      const response = await apiService.post<{ success: boolean; asset: DesignJobAsset; message?: string }>(`${this.endpoint}/${uuid}/assets`, formData)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload asset'
      return {
        success: false,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Upload multiple assets in a batch (creates single timeline entry)
   */
  async uploadAssetsBatch(uuid: string, files: File[], assetType: 'inspiration' | 'reference' | 'client' | 'final' | 'pre_press'): Promise<{ success: boolean; assets?: DesignJobAsset[]; message?: string; error?: string; count?: number; batch?: boolean }> {
    try {
      const formData = new FormData()
      files.forEach(file => {
        formData.append('files[]', file)
      })
      formData.append('asset_type', assetType)
      
      const response = await apiService.post<{ success: boolean; assets: DesignJobAsset[]; message?: string; count?: number; batch?: boolean }>(`${this.endpoint}/${uuid}/assets`, formData)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload assets'
      return {
        success: false,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Delete an asset
   */
  async deleteAsset(uuid: string, assetUuid: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await apiService.delete<{ success: boolean; message?: string }>(`${this.endpoint}/${uuid}/assets/${assetUuid}`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete asset'
      return {
        success: false,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Download an asset
   */
  async downloadAsset(uuid: string, assetUuid: string): Promise<Blob> {
    try {
      // Use axios directly for blob response (like other services do)
      const axios = (await import('axios')).default
      const token = localStorage.getItem('auth_token')
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
      const url = `${baseURL}${this.endpoint}/${uuid}/assets/${assetUuid}/download`
      
      const response = await axios.get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        },
        responseType: 'blob'
      })
      
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to download asset')
    }
  }

  /**
   * View asset (for images - serves draft/original based on job status for final assets)
   */
  async viewAsset(uuid: string, assetUuid: string): Promise<Blob> {
    try {
      // Use axios directly for blob response
      const axios = (await import('axios')).default
      const token = localStorage.getItem('auth_token')
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
      const url = `${baseURL}${this.endpoint}/${uuid}/assets/${assetUuid}/view`
      
      const response = await axios.get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        },
        responseType: 'blob'
      })
      
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to view asset')
    }
  }

  /**
   * Download all completed final assets as ZIP file
   */
  async downloadCompletedFinalAssets(uuid: string): Promise<void> {
    try {
      const axios = (await import('axios')).default
      const token = localStorage.getItem('auth_token')
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
      const url = `${baseURL}${this.endpoint}/${uuid}/assets/final/download-completed`
      
      const response = await axios.get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        },
        responseType: 'blob'
      })
      
      // Create download link and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `design-job-${uuid}-final-assets.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to download completed final assets')
    }
  }

  /**
   * Start timer
   */
  async startTimer(uuid: string): Promise<{ success: boolean; time_entry?: DesignJobTimeEntry; message?: string; error?: string }> {
    try {
      const response = await apiService.post<{ success: boolean; time_entry: DesignJobTimeEntry; message?: string }>(`${this.endpoint}/${uuid}/timer/start`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to start timer'
      return {
        success: false,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Stop timer
   */
  async stopTimer(uuid: string, entryUuid: string): Promise<{ success: boolean; time_entry?: DesignJobTimeEntry; message?: string; error?: string }> {
    try {
      const response = await apiService.post<{ success: boolean; time_entry: DesignJobTimeEntry; message?: string }>(`${this.endpoint}/${uuid}/timer/${entryUuid}/stop`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to stop timer'
      return {
        success: false,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Get timer total
   */
  async getTimerTotal(uuid: string): Promise<TimerTotalResponse> {
    try {
      const response = await apiService.get<TimerTotalResponse>(`${this.endpoint}/${uuid}/timer/total`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get timer total'
      return {
        success: false,
        total_seconds: 0,
        total_hours: 0,
        current_session: null,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Get timeline events
   */
  async getEvents(uuid: string): Promise<EventsResponse> {
    try {
      const response = await apiService.get<EventsResponse>(`${this.endpoint}/${uuid}/events`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get events'
      return {
        success: false,
        events: [],
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Get version history
   */
  async getVersions(uuid: string): Promise<VersionsResponse> {
    try {
      const response = await apiService.get<VersionsResponse>(`${this.endpoint}/${uuid}/versions`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get versions'
      return {
        success: false,
        versions: [],
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Get job data by approval code (for sales - public endpoint)
   */
  async getJobByCode(code: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.get<DesignJobResponse>(`${this.endpoint}/by-code/${code}`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get design job by code'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Get job data by approval token (for customer - public endpoint)
   */
  async getJobByToken(token: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.get<DesignJobResponse>(`${this.endpoint}/by-token/${token}`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get design job by token'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Validate approval code for a specific job UUID (for sales - public endpoint)
   */
  async validateCode(uuid: string, code: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.get<DesignJobResponse>(`${this.endpoint}/${uuid}/validate-code/${code}`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to validate approval code'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Validate approval token for a specific job UUID (for customer - public endpoint)
   */
  async validateToken(uuid: string, token: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.get<DesignJobResponse>(`${this.endpoint}/${uuid}/validate-token/${token}`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to validate approval token'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Get VIP approval status for a design job
   */
  async getVipApprovalStatus(uuid: string): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await apiService.get<{ success: boolean; data: any }>(`${this.endpoint}/${uuid}/vip-approval-status`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get VIP approval status'
      return {
        success: false,
        message: errorMessage
      }
    }
  }

  /**
   * Approve a Design VIP request
   */
  async approveVipRequest(uuid: string, comment?: string): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await apiService.post<{ success: boolean; data: any; message: string }>(`${this.endpoint}/${uuid}/vip-approval/approve`, {
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to approve VIP request'
      return {
        success: false,
        message: errorMessage
      }
    }
  }

  /**
   * Reject a Design VIP request
   */
  async rejectVipRequest(uuid: string, comment?: string): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await apiService.post<{ success: boolean; data: any; message: string }>(`${this.endpoint}/${uuid}/vip-approval/reject`, {
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reject VIP request'
      return {
        success: false,
        message: errorMessage
      }
    }
  }

  /**
   * Approve design job by code (for sales - public endpoint)
   */
  async approveByCode(code: string, email?: string, comment?: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/approve/code/${code}`, {
        email,
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to approve design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Reject design job by code (for sales - public endpoint)
   */
  async rejectByCode(code: string, rejectionReason: string, email?: string, comment?: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/reject/code/${code}`, {
        rejection_reason: rejectionReason,
        email,
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reject design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Approve design job by token (for customer - public endpoint)
   */
  async approveByToken(token: string, email?: string, comment?: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/approve/token/${token}`, {
        email,
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to approve design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Reject design job by token (for customer - public endpoint)
   */
  async rejectByToken(token: string, rejectionReason: string, email?: string, comment?: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/reject/token/${token}`, {
        rejection_reason: rejectionReason,
        email,
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reject design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  async validateQueueCode(uuid: string, code: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.get<DesignJobResponse>(`${this.endpoint}/${uuid}/validate-queue-code/${code}`)
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to validate approval code'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  async approveByQueueCode(code: string, email?: string, comment?: string): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/approve/queue-code/${code}`, {
        email,
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to approve design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  async rejectByQueueCode(
    code: string,
    rejectionReason: string,
    email?: string,
    comment?: string
  ): Promise<DesignJobResponse> {
    try {
      const response = await apiService.post<DesignJobResponse>(`${this.endpoint}/reject/queue-code/${code}`, {
        rejection_reason: rejectionReason,
        email,
        comment: comment || null
      })
      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reject design job'
      return {
        success: false,
        job: {} as DesignJob,
        message: errorMessage,
        error: errorMessage
      }
    }
  }

  /**
   * Get team members for the current user (direct reports and recursive team members).
   * This endpoint is protected by design.queue.view permission.
   */
  async getTeamMembers(): Promise<User[]> {
    try {
      const response = await apiService.get<{ success: boolean; data: User[]; meta: { total: number } }>(`${this.endpoint}/team-members`)
      return response.data || []
    } catch (error: any) {
      console.error('Failed to load team members:', error)
      return []
    }
  }
}

export const designJobService = new DesignJobService()

